/**
 * Commitments — the blocks of the day that are promised to somebody, as
 * opposed to habits, which are promised to yourself.
 *
 * Every time-of-day field on the wire is minutes since local midnight
 * (0–1439), half-open and END-EXCLUSIVE; a start later than the end spans
 * midnight and is rendered as such rather than rejected. Weekday masks are a
 * CSV of weekday indices with Mon=0, and null means every day.
 *
 * The silence policy is the load-bearing control on this page. AUTO_DELETE is
 * the only value under which an unanswered question removes anything, so
 * selecting it is gated behind an explicit confirmation and the default stays
 * ASSUME_HONORED — deleting a real appointment because a question went
 * unanswered is the failure this subsystem exists to prevent.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export type SilencePolicyValue = 'assume_honored' | 'assume_missed' | 'auto_delete';
export type CarryoverPolicyValue = 'stack' | 'merge';

interface SilencePolicyOption {
  value: SilencePolicyValue;
  label: string;
  help: string;
}

// Wire values are the lowercase enum values (models.CommitmentSilencePolicy).
const SILENCE_POLICIES: SilencePolicyOption[] = [
  {
    value: 'assume_honored',
    label: 'Assume it was kept',
    help: 'Silence leaves the block in place and credits it. This is the default.',
  },
  {
    value: 'assume_missed',
    label: 'Assume it was missed',
    help: 'Silence keeps the block but scores it as missed.',
  },
  {
    value: 'auto_delete',
    label: 'Delete the block',
    help: 'Silence deletes the block and its calendar event. Nothing asks again.',
  },
];

const silenceLabel = (value: string): string =>
  SILENCE_POLICIES.find((policy) => policy.value === value)?.label ?? value;

const CARRYOVER_POLICIES: { value: CarryoverPolicyValue; label: string }[] = [
  { value: 'merge', label: 'Merge into one obligation' },
  { value: 'stack', label: 'Stack one obligation per period' },
];

export interface Commitment {
  id: number;
  user_id: number;
  title: string;
  description: string | null;
  location: string | null;
  start_minute: number;
  end_minute: number;
  active_days: string | null;
  occurs_on: string | null;
  effective_from: string | null;
  effective_until: string | null;
  reschedulable: boolean;
  valid_start_minute: number | null;
  valid_end_minute: number | null;
  valid_days: string | null;
  required_hour_class_id: number | null;
  snooze_offsets_minutes: number[] | null;
  silence_policy: string;
  carryover_policy: string;
  publish_to_calendar: boolean;
  is_active: boolean;
  sort_order: number;
  archived_at: string | null;
}

/** Minimal slice of GET /api/hour-classes used by the dialog's select. */
interface HourClassSummary {
  id: number;
  name: string;
}

/** Minutes since local midnight → "HH:MM" wall clock. */
const minutesToTime = (minutes: number): string => {
  const h = String(Math.floor(minutes / 60)).padStart(2, '0');
  const m = String(minutes % 60).padStart(2, '0');
  return `${h}:${m}`;
};

/** "HH:MM" → minutes since local midnight; NaN for an empty/garbled field. */
const timeToMinutes = (value: string): number => {
  const [h, m] = value.split(':').map((part) => parseInt(part, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
  return h * 60 + m;
};

/**
 * An end before the start is not an error — the block runs past midnight, and
 * saying so plainly is the whole point of this helper.
 */
const describeSpan = (startMinute: number, endMinute: number): string => {
  const span = `${minutesToTime(startMinute)}–${minutesToTime(endMinute)}`;
  return endMinute < startMinute ? `${span} (next day)` : span;
};

const describeDays = (activeDays: string | null): string => {
  if (!activeDays) return 'Every day';
  const labels = activeDays
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((part) => DAY_LABELS[Number(part)])
    .filter((label) => label !== undefined);
  return labels.length > 0 ? labels.join(', ') : 'Every day';
};

const daysFromCsv = (activeDays: string | null): number[] => {
  if (!activeDays) return [];
  return activeDays
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
};

const csvFromDays = (days: number[]): string | null =>
  days.length > 0 ? [...days].sort((a, b) => a - b).join(',') : null;

/** Rolling is forward-only, so every offset must be a positive whole minute. */
const offsetsInvalid = (raw: string): boolean => {
  if (raw.trim() === '') return false;
  return raw.split(',').some((part) => {
    const value = Number(part.trim());
    return !Number.isInteger(value) || value <= 0;
  });
};

const parseOffsets = (raw: string): number[] | null => {
  if (raw.trim() === '') return null;
  const values = raw.split(',').map((part) => Number(part.trim()));
  return Array.from(new Set(values)).sort((a, b) => a - b);
};

/** The whole editable form. Times/dates stay as their input text. */
interface CommitmentForm {
  title: string;
  description: string;
  location: string;
  start: string;
  end: string;
  days: number[];
  occursOn: string;
  effectiveFrom: string;
  effectiveUntil: string;
  reschedulable: boolean;
  validStart: string;
  validEnd: string;
  validDays: number[];
  requiredHourClassId: string;
  snoozeOffsets: string;
  silencePolicy: SilencePolicyValue;
  carryoverPolicy: CarryoverPolicyValue;
  publishToCalendar: boolean;
  isActive: boolean;
  sortOrder: string;
}

const EMPTY_FORM: CommitmentForm = {
  title: '',
  description: '',
  location: '',
  start: '09:00',
  end: '10:00',
  days: [],
  occursOn: '',
  effectiveFrom: '',
  effectiveUntil: '',
  reschedulable: false,
  validStart: '',
  validEnd: '',
  validDays: [],
  requiredHourClassId: '',
  snoozeOffsets: '',
  // Never AUTO_DELETE by default — that value is opt-in behind a warning.
  silencePolicy: 'assume_honored',
  carryoverPolicy: 'merge',
  publishToCalendar: false,
  isActive: true,
  sortOrder: '0',
};

const formFromCommitment = (commitment: Commitment): CommitmentForm => ({
  title: commitment.title,
  description: commitment.description || '',
  location: commitment.location || '',
  start: minutesToTime(commitment.start_minute),
  end: minutesToTime(commitment.end_minute),
  days: daysFromCsv(commitment.active_days),
  occursOn: commitment.occurs_on || '',
  effectiveFrom: commitment.effective_from || '',
  effectiveUntil: commitment.effective_until || '',
  reschedulable: commitment.reschedulable,
  validStart:
    commitment.valid_start_minute === null
      ? ''
      : minutesToTime(commitment.valid_start_minute),
  validEnd:
    commitment.valid_end_minute === null
      ? ''
      : minutesToTime(commitment.valid_end_minute),
  validDays: daysFromCsv(commitment.valid_days),
  requiredHourClassId:
    commitment.required_hour_class_id === null
      ? ''
      : String(commitment.required_hour_class_id),
  snoozeOffsets: (commitment.snooze_offsets_minutes || []).join(', '),
  silencePolicy: (commitment.silence_policy as SilencePolicyValue) || 'assume_honored',
  carryoverPolicy: (commitment.carryover_policy as CarryoverPolicyValue) || 'merge',
  publishToCalendar: commitment.publish_to_calendar,
  isActive: commitment.is_active,
  sortOrder: String(commitment.sort_order),
});

/** Empty string for a reason the save button can show; null when valid. */
const formError = (form: CommitmentForm): string | null => {
  if (form.title.trim() === '') return 'A title is required.';
  const start = timeToMinutes(form.start);
  const end = timeToMinutes(form.end);
  if (Number.isNaN(start) || Number.isNaN(end)) return 'Enter a start and an end time.';
  if (start === end) return 'Start and end must differ.';
  const validStartSet = form.validStart.trim() !== '';
  const validEndSet = form.validEnd.trim() !== '';
  if (validStartSet !== validEndSet) {
    return 'Set both ends of the validity window, or neither.';
  }
  if (validStartSet && timeToMinutes(form.validStart) === timeToMinutes(form.validEnd)) {
    return 'The validity window start and end must differ.';
  }
  if (
    form.effectiveFrom !== '' &&
    form.effectiveUntil !== '' &&
    form.effectiveUntil < form.effectiveFrom
  ) {
    return 'The effective end must fall on or after the effective start.';
  }
  if (offsetsInvalid(form.snoozeOffsets)) {
    return 'Snooze offsets must be positive whole minutes, comma separated.';
  }
  return null;
};

const payloadFromForm = (form: CommitmentForm) => {
  const validStartSet = form.validStart.trim() !== '';
  return {
    title: form.title.trim(),
    description: form.description.trim() || null,
    location: form.location.trim() || null,
    start_minute: timeToMinutes(form.start),
    end_minute: timeToMinutes(form.end),
    active_days: csvFromDays(form.days),
    occurs_on: form.occursOn || null,
    effective_from: form.effectiveFrom || null,
    effective_until: form.effectiveUntil || null,
    reschedulable: form.reschedulable,
    valid_start_minute: validStartSet ? timeToMinutes(form.validStart) : null,
    valid_end_minute: validStartSet ? timeToMinutes(form.validEnd) : null,
    valid_days: csvFromDays(form.validDays),
    required_hour_class_id:
      form.requiredHourClassId === '' ? null : Number(form.requiredHourClassId),
    snooze_offsets_minutes: parseOffsets(form.snoozeOffsets),
    silence_policy: form.silencePolicy,
    carryover_policy: form.carryoverPolicy,
    publish_to_calendar: form.publishToCalendar,
    is_active: form.isActive,
    sort_order: Number(form.sortOrder) || 0,
  };
};

const Commitments: React.FC = () => {
  const { pageHeaderSx, sectionGapSx, tableCellPaddingSx } = useLayoutTokens();
  const navigate = useNavigate();
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [hourClasses, setHourClasses] = useState<HourClassSummary[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CommitmentForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // The AUTO_DELETE confirmation. The select is NOT moved until the warning is
  // accepted, so cancelling leaves the previous policy in place untouched.
  const [autoDeleteWarningOpen, setAutoDeleteWarningOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Commitment | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Commitment | null>(null);
  const [busy, setBusy] = useState(false);

  // Only the first load shows the spinner. A refresh after a save or an
  // archive leaves the table on screen and swaps its rows underneath, rather
  // than blanking the page the user is looking at.
  const fetchCommitments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/commitments`, {
        params: { include_archived: showArchived },
      });
      // Degrade to the empty state rather than white-screen on .map if a
      // proxy hiccup serves a non-array body with a 200.
      setCommitments(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load commitments'));
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  const fetchHourClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/hour-classes`);
      setHourClasses(Array.isArray(res.data) ? res.data : []);
    } catch {
      // The select degrades to "No constraint"; the page still works.
      setHourClasses([]);
    }
  }, []);

  useEffect(() => {
    fetchCommitments();
  }, [fetchCommitments]);

  useEffect(() => {
    fetchHourClasses();
  }, [fetchHourClasses]);

  const patchForm = (patch: Partial<CommitmentForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (commitment: Commitment) => {
    setEditingId(commitment.id);
    setForm(formFromCommitment(commitment));
    setError(null);
    setDialogOpen(true);
  };

  // Choosing AUTO_DELETE never takes effect from the select alone: it opens
  // the warning and the value only moves once that is accepted.
  const changeSilencePolicy = (value: SilencePolicyValue) => {
    if (value === 'auto_delete' && form.silencePolicy !== 'auto_delete') {
      setAutoDeleteWarningOpen(true);
      return;
    }
    patchForm({ silencePolicy: value });
  };

  const acceptAutoDelete = () => {
    patchForm({ silencePolicy: 'auto_delete' });
    setAutoDeleteWarningOpen(false);
  };

  const invalidReason = formError(form);

  const save = async () => {
    if (invalidReason) return;
    const payload = payloadFromForm(form);
    setSaving(true);
    try {
      if (editingId === null) {
        await axios.post(`${API_BASE_URL}/api/commitments`, payload);
      } else {
        await axios.put(`${API_BASE_URL}/api/commitments/${editingId}`, payload);
      }
      setDialogOpen(false);
      setError(null);
      await fetchCommitments();
    } catch (err: any) {
      // The dialog stays open on failure so the entry can be corrected.
      setError(getApiErrorMessage(err, 'Failed to save the commitment'));
    } finally {
      setSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE_URL}/api/commitments/${archiveTarget.id}/archive`);
      setError(null);
      await fetchCommitments();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to archive the commitment'));
    } finally {
      setBusy(false);
      setArchiveTarget(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/commitments/${deleteTarget.id}`);
      setError(null);
      await fetchCommitments();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete the commitment'));
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  // A timezone complaint has one specific fix, so say where it lives instead
  // of leaving the API's wording (correct but unactionable) as the last word.
  const errorAlert = error && (
    <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
      {error}
      {/^.*timezone.*$/i.test(error) && (
        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
          Set your account timezone under Profile → Hour classes.
          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
            <Button size="small" variant="outlined" onClick={() => navigate('/profile')}>
              Open Profile
            </Button>
          </Box>
        </Box>
      )}
    </Alert>
  );

  const hourClassName = (id: number | null): string | null => {
    if (id === null) return null;
    return hourClasses.find((entry) => entry.id === id)?.name ?? `#${id}`;
  };

  return (
    <PageShell>
      <Box sx={pageHeaderSx}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h3" component="h1">
              Commitments
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Blocks of time promised to somebody else, with what silence means
              for each one.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
              }
              label="Show archived"
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New commitment
            </Button>
          </Stack>
        </Stack>
      </Box>

      {!dialogOpen && errorAlert}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
            <TableHead>
              <TableRow>
                <TableCell>Commitment</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Days</TableCell>
                <TableCell>Badges</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {commitments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No commitments yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                commitments.map((commitment) => (
                  <TableRow key={commitment.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {commitment.title}
                      </Typography>
                      {commitment.location && (
                        <Typography variant="caption" color="text.secondary">
                          {commitment.location}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {describeSpan(commitment.start_minute, commitment.end_minute)}
                    </TableCell>
                    <TableCell>
                      {commitment.occurs_on
                        ? `Once on ${commitment.occurs_on}`
                        : describeDays(commitment.active_days)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="small"
                          color={
                            commitment.silence_policy === 'auto_delete'
                              ? 'error'
                              : 'default'
                          }
                          label={`Silence: ${silenceLabel(commitment.silence_policy)}`}
                        />
                        {commitment.reschedulable && (
                          <Chip size="small" label="Reschedulable" />
                        )}
                        {commitment.required_hour_class_id !== null && (
                          <Chip
                            size="small"
                            label={`Only in ${hourClassName(
                              commitment.required_hour_class_id,
                            )}`}
                          />
                        )}
                        {commitment.publish_to_calendar && (
                          <Chip size="small" label="On calendar" />
                        )}
                        {!commitment.is_active && <Chip size="small" label="Paused" />}
                        {commitment.archived_at && (
                          <Chip size="small" label="Archived" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => openEdit(commitment)}
                        aria-label={`Edit ${commitment.title}`}
                      >
                        Edit
                      </Button>
                      {!commitment.archived_at && (
                        <Button
                          size="small"
                          startIcon={<ArchiveIcon fontSize="small" />}
                          onClick={() => setArchiveTarget(commitment)}
                          aria-label={`Archive ${commitment.title}`}
                        >
                          Archive
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon fontSize="small" />}
                        onClick={() => setDeleteTarget(commitment)}
                        aria-label={`Delete ${commitment.title}`}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingId === null ? 'New commitment' : 'Edit commitment'}
        </DialogTitle>
        <DialogContent>
          {errorAlert}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Title"
              value={form.title}
              onChange={(e) => patchForm({ title: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => patchForm({ description: e.target.value })}
            />
            <TextField
              fullWidth
              label="Location"
              value={form.location}
              onChange={(e) => patchForm({ location: e.target.value })}
            />

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                type="time"
                label="Start"
                value={form.start}
                onChange={(e) => patchForm({ start: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 140 }}
              />
              <TextField
                type="time"
                label="End"
                value={form.end}
                onChange={(e) => patchForm({ end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 140 }}
              />
              <Typography variant="body2" color="text.secondary">
                {describeSpan(
                  timeToMinutes(form.start) || 0,
                  timeToMinutes(form.end) || 0,
                )}
              </Typography>
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Days (none selected = every day)
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={form.days}
                onChange={(_e, next) => patchForm({ days: next as number[] })}
                aria-label="Active days"
                sx={{ display: 'flex', flexWrap: 'wrap' }}
              >
                {DAY_LABELS.map((label, idx) => (
                  <ToggleButton key={label} value={idx} aria-label={label}>
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField
                type="date"
                label="One-off date"
                value={form.occursOn}
                onChange={(e) => patchForm({ occursOn: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Set for a single occurrence; the weekday mask is then ignored."
                sx={{ minWidth: 200 }}
              />
              <TextField
                type="date"
                label="Effective from"
                value={form.effectiveFrom}
                onChange={(e) => patchForm({ effectiveFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              <TextField
                type="date"
                label="Effective until"
                value={form.effectiveUntil}
                onChange={(e) => patchForm({ effectiveUntil: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={form.reschedulable}
                  onChange={(e) => patchForm({ reschedulable: e.target.checked })}
                />
              }
              label="Reschedulable (may be snoozed to a later slot)"
            />

            {form.reschedulable && (
              <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Where a snooze is allowed to land. Leave the window empty to
                  allow any time of day.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <TextField
                    type="time"
                    label="Valid from"
                    value={form.validStart}
                    onChange={(e) => patchForm({ validStart: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                  <TextField
                    type="time"
                    label="Valid until"
                    value={form.validEnd}
                    onChange={(e) => patchForm({ validEnd: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="commitment-hour-class-label">
                      Required hour class
                    </InputLabel>
                    <Select
                      labelId="commitment-hour-class-label"
                      label="Required hour class"
                      value={form.requiredHourClassId}
                      onChange={(e) =>
                        patchForm({ requiredHourClassId: String(e.target.value) })
                      }
                    >
                      <MenuItem value="">No constraint</MenuItem>
                      {hourClasses.map((hourClass) => (
                        <MenuItem key={hourClass.id} value={String(hourClass.id)}>
                          {hourClass.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Valid days (none selected = every day)
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    value={form.validDays}
                    onChange={(_e, next) => patchForm({ validDays: next as number[] })}
                    aria-label="Valid days"
                    sx={{ display: 'flex', flexWrap: 'wrap' }}
                  >
                    {DAY_LABELS.map((label, idx) => (
                      <ToggleButton
                        key={label}
                        value={idx}
                        aria-label={`${label} valid`}
                      >
                        {label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                <TextField
                  fullWidth
                  label="Snooze offsets (minutes)"
                  value={form.snoozeOffsets}
                  onChange={(e) => patchForm({ snoozeOffsets: e.target.value })}
                  placeholder="15, 30, 60"
                  helperText="Comma-separated, forward only."
                  sx={{ mt: 2 }}
                />
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel id="commitment-silence-policy-label">
                If the question goes unanswered
              </InputLabel>
              <Select
                labelId="commitment-silence-policy-label"
                label="If the question goes unanswered"
                value={form.silencePolicy}
                onChange={(e) =>
                  changeSilencePolicy(e.target.value as SilencePolicyValue)
                }
              >
                {SILENCE_POLICIES.map((policy) => (
                  <MenuItem key={policy.value} value={policy.value}>
                    {policy.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography
              variant="caption"
              color={form.silencePolicy === 'auto_delete' ? 'error' : 'text.secondary'}
            >
              {SILENCE_POLICIES.find((policy) => policy.value === form.silencePolicy)
                ?.help}
            </Typography>

            <FormControl fullWidth>
              <InputLabel id="commitment-carryover-policy-label">
                Missed occurrences
              </InputLabel>
              <Select
                labelId="commitment-carryover-policy-label"
                label="Missed occurrences"
                value={form.carryoverPolicy}
                onChange={(e) =>
                  patchForm({ carryoverPolicy: e.target.value as CarryoverPolicyValue })
                }
              >
                {CARRYOVER_POLICIES.map((policy) => (
                  <MenuItem key={policy.value} value={policy.value}>
                    {policy.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.publishToCalendar}
                    onChange={(e) => patchForm({ publishToCalendar: e.target.checked })}
                  />
                }
                label="Publish to calendar"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => patchForm({ isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
              <TextField
                type="number"
                label="Sort order"
                value={form.sortOrder}
                onChange={(e) => patchForm({ sortOrder: e.target.value })}
                sx={{ width: 120 }}
              />
            </Stack>

            {invalidReason && (
              <Typography variant="caption" color="error">
                {invalidReason}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || invalidReason !== null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* The AUTO_DELETE gate. Cancelling never touches the select, so the
          previous policy — ASSUME_HONORED on a new commitment — stands. */}
      <Dialog
        open={autoDeleteWarningOpen}
        onClose={() => setAutoDeleteWarningOpen(false)}
      >
        <DialogTitle>Let silence delete this block?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            If you do not answer the confirmation for an occurrence, this block
            will be deleted — along with its calendar event — with no further
            prompt. Nothing will ask you again and nothing is kept to undo.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Every other policy keeps the block: &quot;assume it was kept&quot;
            credits it, &quot;assume it was missed&quot; scores it honestly.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* Not labelled "Cancel": this dialog opens on top of the edit
              dialog, and two identically named buttons in one accessibility
              tree is exactly the ambiguity a destructive gate cannot afford. */}
          <Button onClick={() => setAutoDeleteWarningOpen(false)}>
            Keep the current policy
          </Button>
          <Button color="error" variant="contained" onClick={acceptAutoDelete}>
            Yes, delete on silence
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={archiveTarget !== null} onClose={() => setArchiveTarget(null)}>
        <DialogTitle>Archive commitment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            &quot;{archiveTarget?.title}&quot; stops producing new occurrences
            and leaves this list. Everything already recorded against it is
            kept, and it stays visible under &quot;Show archived&quot;.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmArchive} disabled={busy}>
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete commitment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deleting &quot;{deleteTarget?.title}&quot; removes the commitment
            itself. Archive it instead if you only want it to stop recurring.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default Commitments;
